<template>
  <div>
    <div v-if="tags.groups.length > 0" class="mode-switch" role="tablist" aria-label="Insertion mode">
      <button
        type="button"
        role="tab"
        :aria-selected="mode === 'tags'"
        :class="['mode-switch-btn', { active: mode === 'tags' }]"
        title="Insert one tag at a time"
        @click="setMode('tags')"
      >
        Individual Tags
      </button>
      <button
        type="button"
        role="tab"
        :aria-selected="mode === 'table'"
        :class="['mode-switch-btn', { active: mode === 'table' }]"
        title="Insert a real Word table for a repeating region"
        @click="setMode('table')"
      >
        Tables
      </button>
    </div>

    <template v-if="mode === 'table' && tags.groups.length > 0">
      <InsertTableWizard
        :tags="tags"
        @inserted="emit('insert-status', 'Table inserted')"
        @status="forwardWizardStatus"
      />

      <details class="advanced" :open="advancedOpen" @toggle="onToggleAdvanced">
        <summary class="advanced-summary">
          <span class="advanced-label">Advanced</span>
          <span class="advanced-hint">Insert region markers or columns one at a time</span>
        </summary>
        <div class="advanced-body">
          <input
            v-model.trim="search"
            type="search"
            class="search-input"
            placeholder="Search regions and columns"
            aria-label="Search regions and columns"
          />

          <div class="display-toggle">
            <label :title="'Inserts use the real tag names either way.'">
              <input
                type="checkbox"
                :checked="showLabels"
                @change="toggleShowLabels(($event.target as HTMLInputElement).checked)"
              />
              <span>Show labels</span>
            </label>
          </div>

          <div v-if="visibleGroups.length === 0" class="empty-hint">
            No regions match "{{ search }}".
          </div>
          <template v-else>
            <template v-for="group in visibleGroups" :key="group.name">
              <div class="field-label region-heading">
                <span>Region: {{ displayForRegion(group.name) }}</span>
                <span
                  class="info-icon"
                  :title="regionHintTitle(group)"
                  aria-label="How to use this region"
                >&#9432;</span>
              </div>
              <div class="region-card">
                <div class="chip-list">
                  <button
                    type="button"
                    class="chip whole-region"
                    :title="wholeRegionPreview(group)"
                    @click="onInsertWholeRegion(group)"
                  >
                    <span>Whole region</span>
                    <span class="icon" aria-hidden="true">↵</span>
                  </button>
                  <button
                    type="button"
                    class="chip region"
                    :title="regionMarkerTooltip(group.name)"
                    @click="onInsertRegion(group.name)"
                  >
                    <span>{{ regionMarkerLabel(group.name) }}</span>
                    <span class="icon" aria-hidden="true">↵</span>
                  </button>
                  <button
                    v-for="col in visibleColumnsFor(group)"
                    :key="col"
                    type="button"
                    class="chip"
                    :title="tooltipForColumn(col, group.name)"
                    @click="onInsertColumn(col)"
                  >
                    <span>{{ displayForColumn(col, group.name) }}</span>
                    <span class="icon" aria-hidden="true">↵</span>
                  </button>
                </div>
              </div>
            </template>
          </template>
        </div>
      </details>
    </template>

    <template v-else>
      <div class="section-header">
        <h3 class="section-title">Insert Individual Tags</h3>
        <p class="section-desc">Click a tag to insert it at the cursor.</p>
      </div>

      <input
        v-model.trim="search"
        type="search"
        class="search-input"
        placeholder="Search tags"
        aria-label="Search tags"
      />

      <div class="display-toggle">
        <label :title="'Inserts use the real tag names either way.'">
          <input
            type="checkbox"
            :checked="showLabels"
            @change="toggleShowLabels(($event.target as HTMLInputElement).checked)"
          />
          <span>Show labels</span>
        </label>
      </div>

      <template v-if="filteredFieldTags.length > 0">
        <div class="field-label">Header tags</div>
        <div class="chip-list">
          <button
            v-for="tag in filteredFieldTags"
            :key="tag"
            type="button"
            class="chip primary"
            :title="tooltipForField(tag)"
            @click="onInsertField(tag)"
          >
            <span>{{ displayForField(tag) }}</span>
            <span class="icon" aria-hidden="true">↵</span>
          </button>
        </div>
      </template>
      <div v-else-if="normalizedSearch !== ''" class="empty-hint">
        No tags match "{{ search }}".
      </div>
      <div v-else class="empty-hint">
        This source type has no header tags.
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  insertField as insertFieldTag,
  insertColumn as insertColumnTag,
  insertRegion as insertRegionMarkers,
  insertWholeRegion as insertWholeRegionMarkers,
} from '@/services/wordInsertion';
import { toLabel } from '@/services/tagLabels';
import type { SupportedTags, SupportedTagGroup } from '@/types/SupportedTags';
import InsertTableWizard from './InsertTableWizard.vue';

// Persistent insertion mode. Table is the deliberate first-open default so
// non-technical authors see the guided flow; regulars who prefer per-tag
// chips get their last choice back on the next open.
type InsertMode = 'table' | 'tags';
const MODE_KEY = 'thinklodge.suite.insertMode';

function loadMode(): InsertMode {
  if (typeof localStorage === 'undefined') return 'tags';
  const raw = localStorage.getItem(MODE_KEY);
  return raw === 'table' ? 'table' : 'tags';
}

const mode = ref<InsertMode>(loadMode());

function setMode(next: InsertMode): void {
  mode.value = next;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(MODE_KEY, next);
  }
}

// Advanced section in Tables mode holds the manual chips (Whole region /
// Region markers / column chips). Closed by default so first-time users see
// the guided wizard first; state persists so power users don't have to
// re-expand every session.
const ADVANCED_KEY = 'thinklodge.suite.tablesAdvancedOpen';

function loadAdvancedOpen(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(ADVANCED_KEY) === 'true';
}

const advancedOpen = ref<boolean>(loadAdvancedOpen());

function onToggleAdvanced(e: Event): void {
  const open = (e.target as HTMLDetailsElement).open;
  advancedOpen.value = open;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(ADVANCED_KEY, String(open));
  }
}

const props = defineProps<{
  tags: SupportedTags;
}>();

const emit = defineEmits<{
  (e: 'insert-status', message: string): void;
}>();

// Named handler for the wizard's @status event. An inline arrow in the
// template hides the parameter type from ts-loader's stricter inference
// (TS7006), even though vue-tsc accepts it. Naming it here forwards the
// string through the parent's insert-status emit.
function forwardWizardStatus(m: string): void {
  emit('insert-status', m);
}

// If the current source-type has no repeating regions the Table mode has
// nothing to offer — fall back to the chip picker so the pane isn't left
// showing an empty wizard. The user's saved preference is untouched; when
// they switch back to a source-type that has regions, Table returns.
watch(
  () => props.tags.groups.length,
  (n) => {
    if (n === 0 && mode.value === 'table') mode.value = 'tags';
  },
  { immediate: true },
);

// Search state — transient, per-open (not persisted). Kept in sync with the Suite
// TagReferencePanel.vue by the same rule the label derivation follows: same filter
// shape, duplicated across the two repos.
const search = ref('');

// Persist the toggle so a user's preference survives task-pane closes. Default
// to labels since raw CF_UPPER_SNAKE_CASE is noisy for authors who don't
// already know the tag inventory.
const SHOW_LABELS_KEY = 'thinklodge.suite.showLabels';

function loadShowLabels(): boolean {
  if (typeof localStorage === 'undefined') return true;
  const raw = localStorage.getItem(SHOW_LABELS_KEY);
  return raw === null ? true : raw === 'true';
}

const showLabels = ref<boolean>(loadShowLabels());

function toggleShowLabels(next: boolean): void {
  showLabels.value = next;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(SHOW_LABELS_KEY, String(next));
  }
}

// String-concat the braces (not in the template) so the compiler doesn't try to
// interpret literal `{{` inside a mustache — same trick as the Suite frontend.
const OPEN = '{' + '{';
const CLOSE = '}' + '}';

function rawField(tag: string): string {
  return `${OPEN}${tag}${CLOSE}`;
}

// Backend-provided label wins (single source of truth per #2390); falls back to the
// local toLabel() derivation when the DTO doesn't declare a label — either an older
// server without label support or a placeholder source-type whose mapper hasn't
// authored labels yet.
function labelForField(tag: string): string {
  return props.tags.fieldTagLabels?.[tag] ?? toLabel(tag);
}

function labelForColumn(col: string, groupName: string): string {
  return props.tags.groupColumnLabels?.[groupName]?.[col] ?? toLabel(col);
}

function labelForRegion(name: string): string {
  return props.tags.groupLabels?.[name] ?? toLabel(name);
}

// Chip surfaces one view; the tooltip surfaces the OTHER view so hovering is always
// informative. Labels-on → chip is friendly name, tooltip is the raw insert-ready string.
// Labels-off → chip is the raw insert-ready string, tooltip is the friendly name.
function displayForField(tag: string): string {
  return showLabels.value ? labelForField(tag) : rawField(tag);
}

function tooltipForField(tag: string): string {
  return showLabels.value ? rawField(tag) : labelForField(tag);
}

function displayForColumn(col: string, groupName: string): string {
  return showLabels.value ? labelForColumn(col, groupName) : `{{${col}}}`;
}

function tooltipForColumn(col: string, groupName: string): string {
  return showLabels.value ? `{{${col}}}` : labelForColumn(col, groupName);
}

function displayForRegion(name: string): string {
  return showLabels.value ? labelForRegion(name) : name;
}

function regionMarkerLabel(name: string): string {
  return showLabels.value
    ? `${labelForRegion(name)} table markers`
    : '{{TableStart / TableEnd}}';
}

function regionMarkerTooltip(name: string): string {
  return `{{TableStart:${name}}} … {{TableEnd:${name}}}`;
}

// Region hint moved into a hover-title on the info icon next to the region heading —
// saves a full paragraph of vertical space per region. Content: the same explanation
// used to render inline, plus a concrete example the author can imitate.
function regionHintTitle(group: SupportedTagGroup): string {
  const firstCol = group.columns[0] ?? 'Code';
  const secondCol = group.columns[1] ?? 'Name';
  return (
    'Wrap repeating rows between the start and end markers. Use column tags inside each row.\n\n'
    + 'Example:\n'
    + `{{TableStart:${group.name}}}\n`
    + `{{${firstCol}}} {{${secondCol}}}\n`
    + `{{TableEnd:${group.name}}}`
  );
}

// ---- Search filtering (duplicated in ServiceWarranty-UI TagReferencePanel.vue) --------
// Case-insensitive substring against raw tag AND display label. Sync rule per MEMORY.md:
// changes here must land on the Suite side in the same PR family to keep the two
// surfaces behaviorally identical.

function matches(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

const normalizedSearch = computed(() => search.value.trim());

function fieldTagMatches(tag: string): boolean {
  return matches(tag, normalizedSearch.value) || matches(labelForField(tag), normalizedSearch.value);
}

function regionMatches(group: SupportedTagGroup): boolean {
  return matches(group.name, normalizedSearch.value) || matches(labelForRegion(group.name), normalizedSearch.value);
}

function columnMatches(col: string, groupName: string): boolean {
  return matches(col, normalizedSearch.value) || matches(labelForColumn(col, groupName), normalizedSearch.value);
}

const filteredFieldTags = computed(() => {
  if (normalizedSearch.value === '') return props.tags.fieldTags;
  return props.tags.fieldTags.filter(fieldTagMatches);
});

const visibleGroups = computed(() => {
  if (normalizedSearch.value === '') return props.tags.groups;
  return props.tags.groups.filter(
    (group: SupportedTagGroup) =>
      regionMatches(group) || group.columns.some((c: string) => columnMatches(c, group.name)),
  );
});

function visibleColumnsFor(group: SupportedTagGroup): string[] {
  if (normalizedSearch.value === '' || regionMatches(group)) return [...group.columns];
  return group.columns.filter((c: string) => columnMatches(c, group.name));
}

// --------------------------------------------------------------------------------------

async function withStatus(label: string, action: () => Promise<void>): Promise<void> {
  try {
    await action();
    emit('insert-status', `Inserted ${label}`);
  } catch (e) {
    emit('insert-status', e instanceof Error ? `Failed: ${e.message}` : 'Insert failed');
  }
}

function onInsertField(tag: string): void {
  // Status message always uses the raw tag so the user has an audit trail
  // of exactly what landed in the document.
  void withStatus(rawField(tag), () => insertFieldTag(tag));
}

function onInsertColumn(col: string): void {
  void withStatus(`{{${col}}}`, () => insertColumnTag(col));
}

function onInsertRegion(group: string): void {
  void withStatus(
    `{{TableStart:${group}}} … {{TableEnd:${group}}}`,
    () => insertRegionMarkers(group),
  );
}

function wholeRegionPreview(group: SupportedTagGroup): string {
  const cols = group.columns.map((c: string) => `{{${c}}}`).join(' ');
  return `{{TableStart:${group.name}}}\n${cols}\n{{TableEnd:${group.name}}}`;
}

function onInsertWholeRegion(group: SupportedTagGroup): void {
  void withStatus(
    `whole region {{${group.name}}}`,
    () => insertWholeRegionMarkers(group.name, group.columns),
  );
}
</script>

<style scoped>
.search-input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin: 8px 0 8px;
  padding: 6px 10px;
  font: inherit;
  font-size: 12px;
  border: 1px solid #8a8886;
  border-radius: 2px;
  background: #fff;
}

.search-input:focus {
  outline: none;
  border-color: #0078d4;
}

.display-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 4px;
  font-size: 12px;
  color: #605e5c;
}

.display-toggle label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.display-toggle input[type='checkbox'] {
  cursor: pointer;
}

.mode-switch {
  display: flex;
  margin: 8px 0;
  border: 1px solid #8a8886;
  border-radius: 2px;
  overflow: hidden;
}

.mode-switch-btn {
  flex: 1 1 50%;
  padding: 6px 10px;
  font: inherit;
  font-size: 12px;
  color: #201f1e;
  background: #fff;
  border: none;
  border-right: 1px solid #edebe9;
  cursor: pointer;
}

.mode-switch-btn:last-child {
  border-right: none;
}

.mode-switch-btn:hover:not(.active) {
  background: #f3f2f1;
}

.mode-switch-btn.active {
  background: #0078d4;
  color: #fff;
  font-weight: 600;
}

.mode-switch-btn:focus-visible {
  outline: 2px solid #0078d4;
  outline-offset: -2px;
}

.section-header {
  margin: 8px 0 4px;
}

.section-title {
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 600;
  color: #201f1e;
}

.section-desc {
  margin: 0;
  font-size: 12px;
  color: #605e5c;
}

.advanced {
  margin-top: 12px;
  border-top: 1px solid #edebe9;
  padding-top: 8px;
}

.advanced-summary {
  display: flex;
  align-items: baseline;
  gap: 8px;
  cursor: pointer;
  padding: 4px 2px;
  border-radius: 2px;
  list-style: none;
  user-select: none;
}

/* Replace the default disclosure triangle with our own so we can style it —
   Chromium and Firefox handle disclosure markers differently. */
.advanced-summary::-webkit-details-marker {
  display: none;
}

.advanced-summary::before {
  content: '\25B8'; /* ▸ collapsed */
  display: inline-block;
  width: 10px;
  font-size: 10px;
  color: #605e5c;
  transition: transform 120ms ease;
}

.advanced[open] > .advanced-summary::before {
  transform: rotate(90deg);
}

.advanced-summary:hover {
  background: #f3f2f1;
}

.advanced-summary:focus-visible {
  outline: 2px solid #0078d4;
  outline-offset: -2px;
}

.advanced-label {
  font-size: 12px;
  font-weight: 600;
  color: #201f1e;
}

.advanced-hint {
  font-size: 11px;
  color: #605e5c;
}

.advanced-body {
  padding-top: 4px;
}

.region-heading {
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-icon {
  font-size: 14px;
  color: #605e5c;
  cursor: help;
  user-select: none;
}

.empty-hint {
  margin: 12px 0;
  padding: 8px 10px;
  font-size: 12px;
  color: #605e5c;
  background: #f3f2f1;
  border-left: 3px solid #0078d4;
  border-radius: 2px;
}

.chip.whole-region {
  /* Fluent-flavored green (semantic success) — signals the "power" action distinct
     from the region-markers chip alongside it. */
  background: #dff6dd;
}

.chip.whole-region:hover {
  background: #c1eabf;
}
</style>
